import { useCallback, useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'pwaInstallDismissedAt';
// Re-offer roughly a fortnight after a dismissal rather than nagging every visit.
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;

export const isStandalone = () =>
  // matchMedia is absent in some embedded/test environments.
  (typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches) ||
  (window.navigator as unknown as { standalone?: boolean }).standalone === true;

export const isIos = () => {
  const ua = window.navigator.userAgent;
  // iPadOS 13+ reports a Mac user agent, so fall back to touch capability.
  return /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
};

const recentlyDismissed = () => {
  try {
    const at = Number(localStorage.getItem(DISMISSED_KEY));
    return Boolean(at) && Date.now() - at < SNOOZE_MS;
  } catch {
    return false;
  }
};

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(isStandalone);
  const [dismissed, setDismissed] = useState(recentlyDismissed);

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      // Suppress Chrome's mini-infobar so we can offer it in our own UI.
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      /* storage unavailable — dismissal just won't persist */
    }
    setDismissed(true);
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (outcome === 'dismissed') dismiss();
    return outcome === 'accepted';
  }, [deferredPrompt, dismiss]);

  // iOS has no beforeinstallprompt, so the only route is manual instructions.
  const iosNeedsManualInstall = isIos() && !installed;
  const canPrompt = Boolean(deferredPrompt);

  return {
    installed,
    dismissed,
    canPrompt,
    iosNeedsManualInstall,
    shouldOffer: !installed && !dismissed && (canPrompt || iosNeedsManualInstall),
    promptInstall,
    dismiss,
  };
}
